import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Paper,
  Snackbar,
  Alert
} from '@mui/material';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { DataGrid } from '@mui/x-data-grid';
import PrintIcon from '@mui/icons-material/Print';
import axios from 'axios';
import { API_BASE_URL } from '../../constants/constants';
import Loader from '../General/Loader';
import BackButton from '../../components/BackButton';

const ShopTransactionsView = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [totals, setTotals] = useState({
    totalAmount: 0,
    totalCollection: 0
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });

  useEffect(() => {
    fetchTransactions();
  }, [page, pageSize]);

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page + 1,
        limit: pageSize
      });

      if (startDate && endDate) {
        const startDateTime = new Date(startDate);
        startDateTime.setHours(0, 0, 0, 0);
        const endDateTime = new Date(endDate);
        endDateTime.setHours(23, 59, 59, 999);
        
        params.append('startDate', startDateTime.toISOString());
        params.append('endDate', endDateTime.toISOString());
      }

      const response = await axios.get(`${API_BASE_URL}/shop-transactions/successful?${params}`);
      
      if (response.data.data.transactions.length === 0) {
        showSnackbar('No transactions found', 'info');
      }

      setTransactions(response.data.data.transactions);
      setTotalCount(response.data.data.pagination.total);
      setTotals(response.data.data.totals);
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Error fetching transactions', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    if (startDate && endDate) {
      const diffTime = Math.abs(endDate - startDate);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      if (diffDays > 31) {
        showSnackbar('Date range cannot exceed 31 days', 'error');
        return;
      }
      
      if (startDate > endDate) {
        showSnackbar('Start date must be before end date', 'error');
        return;
      }
    }
    
    setPage(0);
    fetchTransactions();
  };

  const handleClear = () => {
    setStartDate(null);
    setEndDate(null);
    setPage(0);
    fetchTransactions();
  };

  const showSnackbar = (message, severity = 'error') => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const handlePrint = () => {
    if (transactions.length === 0) {
      showSnackbar('No data to print', 'warning');
      return;
    }

    const printWindow = window.open('', '_blank', 'width=800,height=600');
    printWindow.document.write(`
      <html>
        <head>
          <title>Shop Transactions Report</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            table { width: 100%; border-collapse: collapse; margin: 20px 0; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f5f5f5; }
            .total { text-align: right; margin-top: 20px; font-weight: bold; }
          </style>
        </head>
        <body>
          <h2 style="text-align: center;">Shop Transactions Report</h2>
          ${startDate && endDate ? `
            <p style="text-align: center;">
              From: ${startDate.toLocaleDateString()} To: ${endDate.toLocaleDateString()}
            </p>
          ` : ''}
          <table>
            <thead>
              <tr>
                <th>Business Name</th>
                <th>Total Amount</th>
                <th>Our Collection</th>
                <th>Date & Time</th>
              </tr>
            </thead>
            <tbody>
              ${transactions.map(tx => `
                <tr>
                  <td>${tx.businessName || 'N/A'}</td>
                  <td>${formatCurrency(tx.totalAmount || 0)}</td>
                  <td>${formatCurrency(tx.ourCollection || 0)}</td>
                  <td>${new Date(tx.createdAt).toLocaleString()}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
          <div class="total">
            <p>Total Amount: ${formatCurrency(totals.totalAmount)}</p>
            <p>Total Collection: ${formatCurrency(totals.totalCollection)}</p>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  const columns = [
    {
      field: 'businessName',
      headerName: 'Business Name',
      width: 200,
      renderCell: (params) => (
        <Typography>{params.row?.businessName || 'N/A'}</Typography>
      )
    },
    {
      field: 'totalAmount',
      headerName: 'Total Amount',
      width: 150,
      renderCell: (params) => (
        <Typography>{formatCurrency(params.row?.totalAmount || 0)}</Typography>
      )
    },
    {
      field: 'ourCollection',
      headerName: 'Our Collection',
      width: 150,
      renderCell: (params) => (
        <Typography>{formatCurrency(params.row?.ourCollection || 0)}</Typography>
      )
    },
    {
      field: 'createdAt',
      headerName: 'Date & Time',
      width: 180,
      renderCell: (params) => (
        <Typography>
          {params.row?.createdAt ? 
            new Date(params.row.createdAt).toLocaleString() : 'N/A'}
        </Typography>
      )
    }
  ];

  const datePickerCustomStyles = {
    width: '100%',
    padding: '8px',
    border: '1px solid #ccc',
    borderRadius: '4px',
    fontSize: '14px'
  };

  return (
     <Box sx={{ position: 'relative', padding: 2 }}>
                  {/* Place BackButton outside the Grid */}
                  <BackButton />
                  <br/>  <br/>
    <Box sx={{ height: 'auto', width: '100%', p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Shop Admin Transactions
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={3}>
            <Typography variant="body2" gutterBottom>Start Date</Typography>
            <DatePicker
              selected={startDate}
              onChange={date => setStartDate(date)}
              maxDate={endDate || new Date()}
              dateFormat="dd/MM/yyyy"
              placeholderText="Select start date"
              style={datePickerCustomStyles}
              className="custom-datepicker"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Typography variant="body2" gutterBottom>End Date</Typography>
            <DatePicker
              selected={endDate}
              onChange={date => setEndDate(date)}
              minDate={startDate}
              maxDate={new Date()}
              dateFormat="dd/MM/yyyy"
              placeholderText="Select end date"
              style={datePickerCustomStyles}
              className="custom-datepicker"
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              variant="contained"
              onClick={handleSearch}
              fullWidth
              sx={{ mt: 3 }}
            >
              Search
            </Button>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              variant="outlined"
              onClick={handleClear}
              fullWidth
              sx={{ mt: 3 }}
            >
              Clear
            </Button>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              variant="outlined"
              color="secondary"
              onClick={handlePrint}
              fullWidth
              startIcon={<PrintIcon />}
              disabled={transactions.length === 0}
              sx={{ mt: 3 }}
            >
              Print
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {loading ? (
        <Loader />
      ) : (
        <>
          <DataGrid
            rows={transactions}
            columns={columns}
            pagination
            paginationMode="server"
            rowCount={totalCount}
            page={page}
            pageSize={pageSize}
            onPageChange={(newPage) => setPage(newPage)}
            onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
            rowsPerPageOptions={[10, 25, 50, 100]}
            disableSelectionOnClick
            autoHeight
            getRowHeight={() => 'auto'}
            getRowId={(row) => row._id}
            sx={{
              '& .MuiDataGrid-cell': {
                whiteSpace: 'normal',
                padding: '8px',
              },
            }}
          />

          <Paper sx={{ p: 2, mt: 2 }}>
            <Grid container spacing={2} justifyContent="flex-end">
              <Grid item>
                <Typography variant="h6">
                  Total Amount: {formatCurrency(totals.totalAmount)}
                </Typography>
              </Grid>
              <Grid item>
                <Typography variant="h6">
                  Total Collection: {formatCurrency(totals.totalCollection)}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
    </Box>
  );
};

export default ShopTransactionsView;